import { useCallback } from 'react'

import { toast } from '@inno-ui/toast'
import { useQueryClient } from '@tanstack/react-query'
import { useMoveItemsToFolderMutation } from 'modules/desktop/modules/files-integration/api/hooks/useMoveItemsToFolder'
import { watchMoveJob } from 'modules/desktop/modules/files-integration/api/socket/watchMoveJob'
import { useTranslation } from 'react-i18next'

import { useDesktopStore } from '../../../stores'

/**
 * Moves files dragged in from a files-app window directly into a desktop folder
 * (when the drag is dropped onto one), rather than placing them as shortcuts on
 * the grid. The files never appear on the desktop, so there's nothing to remove
 * optimistically — we just fire the async move, wait for its socket completion,
 * then refetch the desktop's files list.
 */
export const useMoveFilesItemsIntoFolder = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'desktop.move_to_folder'
  })
  const queryClient = useQueryClient()
  const moveItemsToFolderMutation = useMoveItemsToFolderMutation()

  return useCallback(
    async (folderItemId: string, itemIds: number[]) => {
      if (!itemIds.length) return
      const folder = useDesktopStore
        .getState()
        .items.find(i => i.id === folderItemId)
      // folders are always shortcuts. this also narrows `folder` to
      // ShortcutGridItem for the rest of the scope.
      if (!folder || folder.itemType !== 'shortcut') return

      const folderName = folder.label
      const errorState: { title: string; subTitle?: string } = {
        title: t('failed', { folder: folderName })
      }
      const promise = (async () => {
        try {
          const { jobId } = await moveItemsToFolderMutation.mutateAsync({
            destinationFolderId: folder.fileId,
            sourceItemIds: itemIds
          })
          // Wait for the job-completed socket event before refetching, else the
          // refetch races the move and the files briefly flash back in.
          if (jobId) {
            try {
              await watchMoveJob(jobId)
            } catch {
              // Socket failed — refetch anyway; the move still completes.
            }
          }
          await queryClient.invalidateQueries({ queryKey: ['files-list'] })
        } catch (error) {
          if (error instanceof Error && error.message) {
            errorState.subTitle = error.message
          }
          void queryClient.invalidateQueries({ queryKey: ['files-list'] })
          throw error
        }
      })()
      toast.info({
        asyncHandler: promise,
        asyncStates: {
          loading: {
            title: t('moving', { count: itemIds.length, folder: folderName })
          },
          success: {
            title: t('moved', { count: itemIds.length, folder: folderName })
          },
          error: errorState
        }
      })
    },
    [moveItemsToFolderMutation, queryClient, t]
  )
}
